import React from 'react'
import { Button, Input, Tag, Typography } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import moment from 'moment'
import {
  EMultiOrderStatus,
  EOrderPaymentMethod,
  ERecieveMethod,
  IAdminMultiOrderResponse,
  ISuperOrderItem
} from 'models/orders'
import { ListInfoItem } from 'components/orders/ListInfoItem/ListInfoItem'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { getUserEditPath } from 'routing/utils/getUserEditPath'
import { multiOrderTags } from 'shared/constants/orders'
import { platformColors, platformLabels } from 'shared/constants/platforms'
import { roundToKopecks } from 'shared/utils/roundToKopecks'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { getSignedPrice } from 'shared/utils/getSignedPrice'
import { getDeliveryTypeName } from 'shared/utils/orders/getDeliveryTypeName'
import { getDiscountPayerLabel } from 'shared/utils/orders/getDiscountPayerLabel'
import { getOrderMoneyBreakdown } from 'shared/utils/orders/getOrderMoneyBreakdown'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { SuperOrderPaymentLink } from './SuperOrderPaymentLink'
import styles from './SuperOrderSummary.module.scss'

const EMPTY_VALUE = '—'

interface ISuperOrderSummaryProps {
  superOrder: IAdminMultiOrderResponse
  onOpenTransactions: () => void
  onDownloadInvoice?: () => void
  isInvoiceDownloading?: boolean
  onConfirmInvoicePayment?: () => void
  onCancelUnpaid?: () => void
}

// Заказы супер-заказа оформлялись одной покупкой, но у части групп значение
// всё же расходится — тогда показываем все варианты, а не первый попавшийся
const joinDistinct = (
  values: Array<string | undefined | null>,
  separator = ', '
) => {
  const distinct = Array.from(
    new Set(values.filter((value): value is string => !!value))
  )
  return distinct.length ? distinct.join(separator) : EMPTY_VALUE
}

const getAddress = (order: ISuperOrderItem) =>
  order.delivery_info?.address?.address ??
  order.pvz?.location?.address_full ??
  order.shop_id?.address?.name

const getAddressTitle = (method: ISuperOrderItem['receive_method']) => {
  if (method === ERecieveMethod.PICKUP_POINT) return 'Самовывоз из'
  if (method === ERecieveMethod.PVZ) return 'Доставка до'
  return 'Адрес доставки'
}

const formatDateTime = (date?: string | Date) =>
  date ? moment(date).format('DD.MM.YYYY HH:mm') : EMPTY_VALUE

export const SuperOrderSummary: React.FC<ISuperOrderSummaryProps> = ({
  superOrder,
  onOpenTransactions,
  onDownloadInvoice,
  isInvoiceDownloading,
  onConfirmInvoicePayment,
  onCancelUnpaid
}) => {
  const orders = superOrder.orders ?? []
  const isInvoice = superOrder.payment_method === EOrderPaymentMethod.INVOICE
  const payer = superOrder.invoice_payer
  const isUnpaid = superOrder.status === EMultiOrderStatus.PENDING

  // Складываем доли из снимков каждого заказа. Для старых заказов общий helper
  // сохраняет прежний расчёт. Условия отдельных правил здесь не объединяем
  const money = orders.reduce(
    (acc, order) => {
      const orderMoney = getOrderMoneyBreakdown(
        order,
        order.current_commission ?? 0
      )
      return {
        buyerDeliverySum: acc.buyerDeliverySum + orderMoney.buyerDeliverySum,
        platformDeliverySum:
          acc.platformDeliverySum + orderMoney.platformDeliverySum,
        sellerDeliverySum: acc.sellerDeliverySum + orderMoney.sellerDeliverySum,
        platformCommission:
          acc.platformCommission + orderMoney.platformCommission,
        platformProfit: acc.platformProfit + orderMoney.platformProfit
      }
    },
    {
      buyerDeliverySum: 0,
      platformDeliverySum: 0,
      sellerDeliverySum: 0,
      platformCommission: 0,
      platformProfit: 0
    }
  )
  const deliverySum = roundToKopecks(money.buyerDeliverySum)
  const platformDeliverySum = roundToKopecks(money.platformDeliverySum)
  const sellerDeliverySum = roundToKopecks(money.sellerDeliverySum)
  const platformCommission = roundToKopecks(money.platformCommission)
  const platformProfit = roundToKopecks(money.platformProfit)
  const itemsSum = orders.reduce((acc, order) => acc + (order.sum ?? 0), 0)
  const promoCodes = orders
    .map((order) => order.promo_code?.code)
    .filter((code): code is string => !!code)
  const promoDiscount = orders.reduce(
    (acc, order) =>
      acc + Math.max((order.original_sum ?? 0) - (order.sum ?? 0), 0),
    0
  )
  const discountPayer = getDiscountPayerLabel({
    seller_discount_impact: orders.reduce(
      (acc, order) => acc + (order.promo_details?.seller_discount_impact ?? 0),
      0
    ),
    store_discount_impact: orders.reduce(
      (acc, order) => acc + (order.promo_details?.store_discount_impact ?? 0),
      0
    )
  })
  const comment = joinDistinct(
    orders.map((order) => order.delivery_info?.comment)
  )
  const paidAt = joinDistinct(
    orders.map((order) => (order.paid_at ? formatDateTime(order.paid_at) : undefined))
  )
  const statusTag = multiOrderTags[superOrder.status]
  const buyerId = orders.find((order) => order.user_id?._id)?.user_id?._id
  const buyerName = joinDistinct(
    orders.map(
      (order) => order.delivery_info?.full_name ?? order.user_id?.full_name
    )
  )
  const receiveMethods = Array.from(
    new Set(orders.map((order) => order.receive_method))
  )
  const addressTitle =
    receiveMethods.length === 1
      ? getAddressTitle(receiveMethods[0])
      : 'Адрес получения'
  const address = joinDistinct(orders.map(getAddress), '\n')

  return (
    <div className={styles.summary}>
      <div className={styles.block}>
        <Typography.Title level={5} className={styles.blockTitle}>
          Покупатель
        </Typography.Title>
        <ListInfoItem
          name='ФИО'
          value={
            buyerId ? (
              <CardLink to={getUserEditPath(buyerId)} newTab>
                {buyerName === EMPTY_VALUE ? 'Без имени' : buyerName}
              </CardLink>
            ) : (
              buyerName
            )
          }
        />
        <ListInfoItem
          name='Телефон'
          value={joinDistinct(
            orders.map((order) => {
              const tel = order.delivery_info?.tel ?? order.user_id?.tel
              return tel ? phoneMaskStatic(tel) : undefined
            })
          )}
        />
        <ListInfoItem
          name='Почта'
          value={joinDistinct(orders.map((order) => order.user_id?.email))}
          wrapValue
        />
        <ListInfoItem
          name='Платформа'
          value={
            superOrder.platform ? (
              <Tag color={platformColors[superOrder.platform]} style={{ marginRight: 0 }}>
                {platformLabels[superOrder.platform]}
              </Tag>
            ) : (
              'Не определено'
            )
          }
        />
        <ListInfoItem
          name='Оформлен'
          value={formatDateTime(superOrder.createdAt)}
        />
      </div>
      <div className={styles.block}>
        <Typography.Title level={5} className={styles.blockTitle}>
          Доставка
        </Typography.Title>
        <ListInfoItem
          name='Способ'
          value={joinDistinct(
            orders.map((order) =>
              getDeliveryTypeName(
                order.receive_method,
                order.delivery_info?.provider ?? order.pvz?.provider,
                order.pvz?.name
              )
            )
          )}
        />
        <div className={styles.address}>
          <Typography.Text>{addressTitle}:</Typography.Text>
          <Input.TextArea
            value={address}
            readOnly
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
        </div>
        <ListInfoItem
          name='Доплатил покупатель'
          value={
            deliverySum ? getNormalizedPrice(deliverySum) : 'Бесплатная'
          }
          wrapValue
        />
        <ListInfoItem
          name='За счёт площадки'
          value={getNormalizedPrice(platformDeliverySum)}
        />
        <ListInfoItem
          name='За счёт продавцов'
          value={getNormalizedPrice(sellerDeliverySum)}
        />
        {comment !== EMPTY_VALUE && (
          <ListInfoItem name='Комментарий' value={comment} />
        )}
      </div>
      <div className={styles.block}>
        <Typography.Title level={5} className={styles.blockTitle}>
          Оплата
        </Typography.Title>
        <ListInfoItem
          name='Статус'
          value={
            <Tag color={statusTag.color} style={{ marginRight: 0 }}>
              {statusTag.label}
            </Tag>
          }
        />
        <ListInfoItem
          name='Способ оплаты'
          value={isInvoice ? 'По счёту' : 'Картой'}
        />
        {isInvoice && payer && (
          <>
            <ListInfoItem name='Плательщик' value={payer.name} wrapValue />
            <ListInfoItem name='ИНН' value={payer.inn} />
            {payer.kpp && <ListInfoItem name='КПП' value={payer.kpp} />}
          </>
        )}
        <ListInfoItem
          name='Стоимость товаров'
          value={getNormalizedPrice(itemsSum)}
        />
        <ListInfoItem
          name='Стоимость доставки'
          value={getNormalizedPrice(deliverySum)}
        />
        <ListInfoItem
          name='Сумма супер-заказа'
          value={getNormalizedPrice(superOrder.sum)}
        />
        {!!promoCodes.length && (
          <>
            <ListInfoItem
              name='Промокод'
              value={
                <>
                  {Array.from(new Set(promoCodes)).map((code) => (
                    <Tag key={code} color='green' style={{ marginRight: 0 }}>
                      {code}
                    </Tag>
                  ))}
                </>
              }
            />
            <ListInfoItem
              name='Скидка'
              value={getNormalizedPrice(promoDiscount)}
            />
            <ListInfoItem name='Скидку оплатил' value={discountPayer} />
          </>
        )}
        <ListInfoItem
          name='Комиссия площадки'
          value={getNormalizedPrice(platformCommission)}
        />
        <ListInfoItem
          name='Заработок площадки'
          value={getSignedPrice(platformProfit)}
          valueStyle={{ color: platformProfit < 0 ? '#cf1322' : '#389e0d' }}
        />
        <ListInfoItem name='Оплачен' value={paidAt} />
        <ListInfoItem
          name='Платёж'
          value={
            isInvoice ? (
              superOrder.payment_id ? 'Перевод по счёту' : EMPTY_VALUE
            ) : superOrder.payment_id ? (
              <Typography.Link
                href={`https://yookassa.ru/my/payments?search=${superOrder.payment_id}`}
                target='_blank'
              >
                ЮKassa
              </Typography.Link>
            ) : (
              EMPTY_VALUE
            )
          }
        />
        {isInvoice && onDownloadInvoice && (
          <Button
            block
            size='middle'
            htmlType='button'
            icon={<FilePdfOutlined />}
            loading={isInvoiceDownloading}
            style={{ marginTop: 15 }}
            onClick={onDownloadInvoice}
          >
            Скачать счёт
          </Button>
        )}
        {isInvoice && isUnpaid && onConfirmInvoicePayment && (
          <Button
            block
            type='primary'
            size='middle'
            htmlType='button'
            icon={<CheckCircleOutlined />}
            style={{ marginTop: 10 }}
            onClick={onConfirmInvoicePayment}
          >
            Оплата по счёту получена
          </Button>
        )}
        {!isInvoice && isUnpaid && orders[0] && (
          <SuperOrderPaymentLink orderId={orders[0]._id} />
        )}
        {isUnpaid && onCancelUnpaid && (
          <Button
            block
            danger
            size='middle'
            htmlType='button'
            icon={<CloseCircleOutlined />}
            style={{ marginTop: 10 }}
            onClick={onCancelUnpaid}
          >
            Отменить супер-заказ
          </Button>
        )}
        <Button
          block
          size='middle'
          htmlType='button'
          icon={<UnorderedListOutlined />}
          style={{ marginTop: 15 }}
          onClick={onOpenTransactions}
        >
          Транзакции супер-заказа
        </Button>
      </div>
    </div>
  )
}
